import { Doc, Id } from "../convex/_generated/dataModel";

export enum ChatType {
    Onboarding = "onboarding"
}

export enum AssistantTypeEnum {
    CBT = "CBT",
    CR = "CR",
    WS = "WS",
    First = "First",
    Last = "Last",
}

export interface AssistantType {
    _id: Id<"assistant_types">;
    name: AssistantTypeEnum;
    description: string;
    required: boolean;
}

export const DefaultNextMessage = 'Next';

export interface ChatMessage {
    body: string;
    content: string;
    author: MessageRole;
    role: MessageRole;
    id?: string;
    suggestions?: SuggestedAction[];
    showSuggestions?: boolean;
    isLastMessage?: boolean;
    tasks?: string[];
    completed?: boolean;
    rawMessage?: string;
    hidden?: boolean;
    context?: string;
    // Debug trace for admin debugging
    debug_trace?: Array<{
        type: string;
        tool_name?: string;
        tool_args?: any;
        result?: any;
        message?: string;
        timestamp: string;
    }>;
}

export interface SuggestedAction {
    content: string;
    action: SuggestedActionType;
}

export enum SuggestedActionType {
    Submit = "submit",
    Exit = "exit",
    YourChat = "your_chat",
    MultipleChoice = "multiple_choice",
    Next = "next",
    UploadResume = "upload_resume",
    MoreOnThis = "more_on_this"
}

export interface SentMessageResponse {
    messages?: Doc<"messages">[];
    messageId: Id<"messages">;
}

export interface MessageMetadata {
    response: string;
    suggested_replies?: string[];
    answers?: string[];
    complete?: boolean;
}

export enum MainRoutes {
    Base = '/',
    Landing = '/',
    Chat = '/chat',
    Progress = '/progress',
    Skills = '/skills',
    TraitDetails = '/skills/:traitId',
    BrowseAll = '/progress/browse-all',
    Admin = '/admin',
    PersonalAssistant = '/personal-assistant',
    Profile = '/profile',
    Subscribe = '/subscribe',
    Unsubscribe = '/unsubscribe/:unsubscribeToken',
    LegalAcceptance = '/legal-acceptance',
    MyChat = '/my-chat',
    FAQ = '/faq'
}

export enum AdminRoutes {
    Legal = '/admin/legal',
    Assistants = '/admin/assistants',
    Skills = '/admin/skills',
    Progress = '/admin/progress',
    System = '/admin/system',
    ViewSystemAssistant = '/admin/system',
    Context = '/admin/context',
    EmbeddingsConfig = '/admin/embeddings-config',
    AssistantMetadata = '/admin/metadata',
    ManualContextUpload = '/admin/manual-context-upload',
    Documentation = '/admin/documentation',
    KnowledgeBase = '/admin/knowledge-base',
    KnowledgeBaseAnalytics = '/admin/knowledge-base/analytics',
    Subscribers = '/admin/subscribers',
    Sessions = '/admin/sessions',
    SessionDetail = '/admin/sessions/:sessionId',
    SessionInsights = '/admin/sessions/:sessionId/insights',
    UserProgress = '/admin/user-progress',
    Users = '/admin/users',
    UserDetail = '/admin/users/:userId',
    CheckinInstructions = '/admin/checkin-instructions',
    LevelConfig = '/admin/level-config',
    AcornConfig = '/admin/acorn-config',
    Feedback = '/admin/feedback',
    IntroSlides = '/admin/intro-slides',
    Tools = '/admin/tools',
    InnerWeatherQuotes = '/admin/inner-weather-quotes',
    FAQ = '/admin/faq',
    UserAnalytics = '/admin/user-analytics',
    EngagementAnalytics = '/admin/engagement-analytics',
    ScreenTimeAnalytics = '/admin/screen-time-analytics',
    AuditLog = '/admin/audit-log'
}

export enum MessageRole {
    User = 'user',
    Assistant = 'assistant',
    System = 'system'
}

export interface Session {
    _id: Id<"sessions">;
    type?: ChatType;
    assistant_id?: Id<"assistants">;
    thread_id?: string;
    user_id?: Id<"users">;
    isComplete?: boolean;
    openai_assistant_id?: string;
    greeting?: string;
    multiple_choice?: boolean;
    multiple_choice_questions?: MultipleChoiceQuestionResponse[];
    messages?: ChatMessage[];
    upload_resume_button_caption?: string;
    show_upload_resume_button?: boolean;
    response_in_progress?: boolean;
    conversation_id?: string;
    temperature?: number;
    skill_id?: Id<"skills">;
}

export interface CompletedSession {
    _id: Id<"completed_sessions">;
    session_id: Id<"sessions">;
    skill_id: Id<"skills">;
    assistant_id: Id<"assistants">;
    user_id: Id<"users">;
    completed_timestamp?: string;
}

export interface Assistant {
    _id: Id<"assistants">;
    name: string;
    type: Id<"assistant_types">;
    typeName: string;
    last_modified_timestamp?: string;
    last_modified_by?: Id<"users">;
    last_modified_by_name?: string;
    is_active: boolean;
    is_draft: boolean;
    goal: string;
    display_name: string;
    description?: string;
    openai_assistant_id: string;
    _creationTime?: string;
    purpose: string;
    greeting: string;
    system_response_schema: string;
    multiple_choice: boolean;
    multiple_choice_questions: MultipleChoiceQuestion[];
    prompt: string;
    show_exit_button?: boolean;
    exit_button_caption?: string;
    show_your_chat_button?: boolean;
    your_chat_button_caption?: string;
    show_upload_resume_button?: boolean;
    upload_resume_button_caption?: string;
}

export interface SkillAssistant {
    _id: Id<"skill_assistants">;
    skill_id: Id<"skills">;
    assistant_id: Id<"assistants">;
    sequence: number;
    name: string;
    description?: string;
    assistantName?: string;
    assistantDisplayName?: string;
    assistantType?: string;
}

export interface UserProgress {
    _id: Id<"user_progress">;
    user_id: Id<"users">;
    n_score: number;
    wb_score: number;
    se_score: number;
    skills: UserSkill[];
}

export interface UserSkill {
    skill_id: Id<"skills">;
    sequence: number;
    completed_count: number;
    total_count: number;
    percentage_complete: number;
    completed_timestamp?: number;
    skill_info?: any;
}

export enum Permissions {
    Admin = "admin"
}

export const SystemMessagePrefix = "@system::";

export const SystemMessages = {
    MultipleChoiceInitiateInteraction: `${SystemMessagePrefix} begin interaction`,
    CalculateScores: `${SystemMessagePrefix} calculate scores`,
    GenerateSummary: `${SystemMessagePrefix} generate summary`,
    ResumeSummary: `${SystemMessagePrefix} use this resume summary as a context, don't reply to this message directly`,
    GenerateSummaryForPersonalAssistant: `${SystemMessagePrefix} generate summary for personal assistant`,
    UpdatePersonalAssistant: `${SystemMessagePrefix} context`,
    ResumeUploaded: `${SystemMessagePrefix} user has uploaded a resume. Acknowledge the upload, reference some data from the resume summary and continue the conversation.`,
    NoResume: `${SystemMessagePrefix} user does not have a resume. Reassure them that we will help them build one down the line and continue the conversation.`,
    EmbeddingsContext: `@context:: user context: `,
    GlobalContextEmbeddings: `@context:: global context(use it as a context gathered from web, not personal to the user): `,
    UserMetadata: `${SystemMessagePrefix} the following is the user metadata. Do not reply to this message directly. This is what we know about the user so far, use it make the conversation more personal: `,
    CurrentDate: `${SystemMessagePrefix} current date: `,
    NextQuestion: `${SystemMessagePrefix} do not reply to this message directly. This is the next question to ask the user: `
}

export enum SystemAssistantNames {
    PersonalAssistant = "Personal assistant",
    ScoringAssistant = "Scoring assistant",
    SummaryAssistant = "Summary assistant",
    GlobalSummaryAssistant = "Global summary assistant",
    ContextReceiver = "Context receiver",
    ResumeSummary = "Resume summary",
    EvaluatePost = "Evaluate post",
    CombineTwoSummaries = "Combine two summaries",
    SplitTextIntoChunksSemantically = "Split text into chunks semantically",
    ExtractUserMetadata = "Extract user metadata",
    GenerateNextPersonalQuestion = "Generate next personal question",
    NextSessionsRecommendation = "Next Sessions Recommendation",

}

export enum WorkerMessageType {
    CalculateScores = 'calculateScores',
    GenerateSummary = 'generateSummary',
    GenerateSummaryForPersonalAssistant = 'generateSummaryForPersonalAssistant',
    UpdatePersonalAssistant = 'updatePersonalAssistant',
    ContextReceiver = 'contextReceiver'
}

export interface SystemAssistant {
    _id: Id<"system_assistants">;
    name: string;
    instructions: string;
    previous_instructions?: string;
    previous_response_schema?: string;
    response_schema: string;
    openai_assistant_id?: string;
    timestamp_modified: string;
    modified_by: Id<"users">;
}

export interface MultipleChoiceQuestion {
    question: string;
    options: string[];
}

export interface MultipleChoiceQuestionResponse {
    question: string;
    selected_option: string;
}

export interface EmbeddingConfig {
    _id: Id<"embeddings_config">;
    name: string;
    description?: string;
    cosine_similarity_threshold: number;
    modified_by: Id<"users">;
    timestamp_modified: string;
  }

export const DefaultPersonalAssistantInstructions = `You are a supportive and empathetic personal assistant focused on helping users improve their well-being and personal growth. Your role is to:

1. Engage in friendly, conversational dialogue while maintaining professional boundaries
2. Listen actively and respond with empathy to users' concerns
3. Provide constructive feedback and gentle encouragement
4. Help users develop practical strategies for managing daily challenges
5. Maintain a positive and solution-focused approach
6. Ask clarifying questions when needed to better understand the user's situation
7. Respect user privacy and confidentiality at all times

Remember to:
- Keep responses clear and concise
- Use supportive and non-judgmental language
- Focus on the user's strengths and potential
- Encourage self-reflection and personal insight`

export const DefaultPersonalAssistantResponseSchema = `{
  "name": "personal_assistant_response",
  "strict": true,
  "schema": {
    "type": "object",
    "properties": {
      "response": {
        "type": "string",
        "description": "The response to the user's message."
      }
    },
    "required": [
      "response"
    ],
    "additionalProperties": false
  }
}`

// ============ KNOWLEDGE BASE TYPES ============

export interface KnowledgeBaseType {
  _id: Id<"knowledge_base_types">;
  name: string;
  displayName: string;
  description?: string;
  icon?: string;
  recommendedGranularity?: string;
  isActive: boolean;
  sortOrder?: number;
  createdAt: number;
  createdBy: Id<"users">;
}

export interface KnowledgeBaseCategory {
  _id: Id<"knowledge_base_categories">;
  name: string;
  displayName: string;
  description?: string;
  color?: string;
  isActive: boolean;
  usageCount: number;
  createdAt: number;
  createdBy: Id<"users">;
}

export interface KnowledgeBaseTag {
  _id: Id<"knowledge_base_tags">;
  name: string;
  displayName: string;
  usageCount: number;
  isActive: boolean;
  createdAt: number;
  createdBy: Id<"users">;
}

export interface KnowledgeBaseGroup {
  _id: Id<"knowledge_base_groups">;
  name: string;
  description?: string;
  categoryId?: Id<"knowledge_base_categories">;
  isActive: boolean;
  sortOrder?: number;
  createdAt: number;
  updatedAt: number;
  createdBy: Id<"users">;
  updatedBy: Id<"users">;
}

export interface KnowledgeBaseEntry {
  _id: Id<"knowledge_base">;
  typeId: Id<"knowledge_base_types">;
  title: string;
  content: string;
  categoryId?: Id<"knowledge_base_categories">;
  tagIds?: Id<"knowledge_base_tags">[];
  parentGroupId?: Id<"knowledge_base_groups">;
  sequenceInGroup?: number;
  targetAudience?: "all" | "new_users" | "active_users";
  isActive: boolean;
  sortOrder?: number;
  // Usage tracking
  timesUsed?: number;
  lastUsedAt?: number;
  lastUsedByUserId?: Id<"users">;
  createdAt: number;
  updatedAt: number;
  createdBy: Id<"users">;
  updatedBy: Id<"users">;
}

export interface KnowledgeBaseUsage {
  _id: Id<"knowledge_base_usage">;
  entryId: Id<"knowledge_base">;
  messageId: Id<"evergreen_messages">;
  userId: Id<"users">;
  contextId?: string;
  timestamp: number;
  searchQuery?: string;
  relevanceScore?: number;
}

// ============ EVERGREEN CHAT TYPES ============

export enum EvergreenWidgetType {
  SkillProgress = "skill_progress",
  SessionRecommendation = "session_recommendation",
  RelevantSessions = "relevant_sessions",
  MindsetScores = "mindset_scores",
  ActionButtons = "action_buttons",
  SessionList = "session_list",
  QuickReplies = "quick_replies",
}

export interface SkillProgressWidgetData {
  skill_id: string;
  skill_name: string;
  completed_count: number;
  total_count: number;
  summary?: string;
}

export interface SessionRecommendationWidgetData {
  sessions: Array<{
    id: string;
    display_name: string;
    description?: string;
    skill_name?: string;
  }>;
  reasoning?: string;
}

export interface MindsetScoresWidgetData {
  neuroticism: { current: number; previous?: number };
  well_being: { current: number; previous?: number };
  self_efficacy: { current: number; previous?: number };
}

export interface ActionButtonWidgetData {
  buttons: Array<{
    label: string;
    action: string; // "start_session" | "navigate" | "submit_prompt"
    payload: Record<string, string>;
  }>;
}

export interface QuickRepliesWidgetData {
  replies: string[];
}

export type EvergreenWidgetData =
  | { type: EvergreenWidgetType.SkillProgress; data: SkillProgressWidgetData }
  | { type: EvergreenWidgetType.SessionRecommendation; data: SessionRecommendationWidgetData }
  | { type: EvergreenWidgetType.MindsetScores; data: MindsetScoresWidgetData }
  | { type: EvergreenWidgetType.ActionButtons; data: ActionButtonWidgetData }
  | { type: EvergreenWidgetType.QuickReplies; data: QuickRepliesWidgetData };

export interface EvergreenMessage {
  _id: Id<"evergreen_messages">;
  user_id: Id<"users">;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: string;
  widget?: EvergreenWidgetData;
  context_id?: string;
  hidden?: boolean;
}

export interface EvergreenChatState {
  _id: Id<"evergreen_chat_state">;
  user_id: Id<"users">;
  conversation_id?: string;
  last_activity: string;
  response_in_progress?: boolean;
  active_context_id?: string;
}

export interface EvergreenEntryPoint {
  contextId: string;
  systemMessage: string;
  autoSubmitPrompt?: string;
}

// Factory functions for creating entry points
export const EvergreenEntryPoints = {
  skillProgress: (skillId: string, skillName: string): EvergreenEntryPoint => ({
    contextId: `skill_${skillId}`,
    systemMessage: `User is viewing their progress on the "${skillName}" skill. They may ask questions about this skill, their progress, or request recommendations for next sessions within this skill.`,
    autoSubmitPrompt: `Tell me more about my progress on ${skillName}.`,
  }),

  sessionComplete: (sessionName: string, skillName: string): EvergreenEntryPoint => ({
    contextId: `session_complete_${Date.now()}`,
    systemMessage: `User just completed the "${sessionName}" session and wants to explore the topics from it further.

IMPORTANT: You MUST call these tools to provide accurate information:
1. get_completed_sessions - to see what they just finished
2. get_skill_progress - to check their progress on the relevant skill

Congratulate them briefly, then dive deeper into the themes and topics covered in "${sessionName}". Help them reflect on what they learned and how to apply it. Do NOT recommend the next session — the user specifically chose to stay on this topic.`,
    autoSubmitPrompt: `Let's chat more about some of the topics covered in the "${sessionName}" session I just finished.`,
  }),

  general: (): EvergreenEntryPoint => ({
    contextId: "general",
    systemMessage: "User opened the general chat. Help them with their career journey.",
  }),

  mindsetReview: (): EvergreenEntryPoint => ({
    contextId: "mindset_review",
    systemMessage: "User wants to review their mindset scores and understand their progress.",
    autoSubmitPrompt: "How am I doing overall? Can you explain my scores?",
  }),

  custom: (contextId: string, systemMessage: string, autoSubmitPrompt?: string): EvergreenEntryPoint => ({
    contextId,
    systemMessage,
    autoSubmitPrompt,
  }),
} as const;
